import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid2,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  Upload,
  Eye,
  Clock,
  AlertCircle,
  CheckCircle,
  Download,
  X,
  ArrowBigUp,
} from 'lucide-react';
import {
  FormAnswers,
  FormSubmission,
  ProjectContext,
} from '../../../types/forms.types';
import { Project, ProjectMember } from '../../../types/project.types';
import { formsService } from '../../../services/forms.service';
import { Questionnaire } from '../../../types/questionnaire.types';
import { Region } from '../../../types/fair.types';
import { usersService } from '../../../services/users.service';
import { getRegionById } from '../../../services/regions.services';
import { questionnaireService } from '../../../services/questionnaire.service';
import { useAuth } from '../../../contexts/AuthContext';
import FormFiller from './FormFiller';
import { StudentProfile } from '../../../types/auth.types';
import { projectsService } from '../../../services/projects.service';

interface ProjectDocumentsProps {
  projectId: string;
  projectName: string;
  projectMembers: ProjectMember[];
}

interface ProjectDocumentsDialogProps extends ProjectDocumentsProps {
  open: boolean;
  onClose: () => void;
}

const ProjectDocuments: React.FC<ProjectDocumentsDialogProps> = ({
  open,
  onClose,
  projectId,
  projectName,
  projectMembers,
}) => {
  const [forms, setForms] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formType, setFormType] = useState<string>('');
  const [responsibleStudentId, setResponsibleStudentId] = useState<string>('');
  const [responsibleStudentName, setResponsibleStudentName] =
    useState<string>('');
  const [uploading, setUploading] = useState(false);

  const [formFillerOpen, setFormFillerOpen] = useState(false);
  const [selectedQuestionnaireID, setSelectedQuestionnaireID] =
    useState<string>('');

  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [region, setRegion] = useState<Region | null>(null);
  const { authStatus } = useAuth();
  const [user, setUser] = useState<StudentProfile | null>(null);
  const [project, setProject] = useState<Project | null>(null);

  const fetchUserRegion = useCallback(async () => {
    setLoading(true);
    try {
      const userId = authStatus.user?.uid;

      if (!userId) {
        setError('User information not available.');
        setLoading(false);
        return;
      }
      setUser(
        (await usersService.getUserAccount(userId)) as unknown as StudentProfile
      );
      setProject((await projectsService.getProject(projectId)) as Project);
      const userRegionId = await usersService.getUserRegion(userId);
      const userRegion = await getRegionById(userRegionId);

      if (!userRegion) {
        setError('You are not associated with any region.');
        setLoading(false);
        return;
      }

      setRegion({ ...userRegion });
    } catch (error) {
      console.error('Error fetching regions:', error);
      setError('Failed to load region information');
    } finally {
      setLoading(false);
    }
  }, [authStatus.user?.uid, projectId]);

  useEffect(() => {
    fetchUserRegion();
  }, [fetchUserRegion]);

  const fetchQuestionnaires = useCallback(async () => {
    setLoading(true);
    try {
      if (region?.id && project?.fairId) {
        const subscribe =
          questionnaireService.subscribeToActiveQuestionnairesByFair(
            (updatedQuestionnaires) => {
              setQuestionnaires(updatedQuestionnaires);
              setLoading(false);
            },
            project.fairId
          );
        return () => subscribe();
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      setError('Failed to load region information');
    } finally {
      setLoading(false);
    }
  }, [region?.id, project?.fairId]);

  useEffect(() => {
    fetchQuestionnaires();
  }, [region, fetchQuestionnaires]);

  useEffect(() => {
    const unsubscribe = formsService.subscribeToProjectForms(
      projectId,
      (updatedForms) => {
        setForms(updatedForms);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'needs_revision':
        return 'warning';
      case 'in_review':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle size={16} />;
      case 'rejected':
      case 'needs_revision':
        return <AlertCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !responsibleStudentId) return;

    try {
      setUploading(true);
      setError(null);
      var existingForm = forms.find((obj) => obj.formType === formType);

      var formAnswers = {
        projectId,
        formType,
        lastUpdated: new Date(),
        answers: {},
      } as FormAnswers;

      if (existingForm) {
        await formsService.uploadNewVersion(
          existingForm.id,
          selectedFile,
          formAnswers,
          responsibleStudentId
        );
      } else {
        const projectContext: ProjectContext = {
          projectId,
          projectName,
          responsibleStudentId,
          responsibleStudentName,
          assignedAt: new Date(),
        };

        await formsService.submitProjectForm(
          projectContext,
          selectedFile,
          formAnswers,
          formType,
          false // isRequired - could be determined by form type or passed as prop
        );
      }

      setUploadDialogOpen(false);
      resetUploadForm();
    } catch (err) {
      setError('Failed to upload form. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setFormType('');
    setResponsibleStudentId('');
    setResponsibleStudentName('');
  };

  const handleEditButton = (newQuestionnaireID: string) => {
    setSelectedQuestionnaireID(newQuestionnaireID);
    setFormFillerOpen(true);
  };

  const getQuestionnaireSubmission = (questionnaireID: string) => {
    return forms.find((obj) => obj.formType === questionnaireID);
  };

  const getQuestionnaireStatus = (questionnaireID: string) => {
    var form = getQuestionnaireSubmission(questionnaireID);
    if (form) {
      return form.status;
    } else {
      return 'No Submissions';
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' },
      }}
    >
      <DialogTitle>
        <Box className="flex justify-between items-center">
          <Typography variant="h5">Project Documents</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className="space-y-6">
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            className="mb-4"
          >
            {error}
          </Alert>
        )}

        {/* Forms */}
        <Box className="space-y-4">
          {['Required', 'Optional'].map((status) => (
            <Box>
              {questionnaires.filter((obj) => obj.status === status).length >
                0 && (
                <Typography
                  sx={{ flex: '1 1 100%' }}
                  variant="h6"
                  id="tableTitle"
                  component="div"
                  pb={2}
                  pt={2}
                >
                  {status}
                </Typography>
              )}
              {questionnaires
                .filter((obj) => obj.status === status)
                .map(
                  (questionnaire) =>
                    questionnaire && (
                      <Box key={questionnaire.id}>
                        <Accordion
                          key={questionnaire.id}
                          elevation={2}
                          defaultExpanded
                        >
                          <AccordionSummary
                            expandIcon={<ArrowBigUp />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                          >
                            <Grid2 container spacing={1} sx={{ width: '98%' }}>
                              <Grid2
                                display="flex"
                                justifyContent="left"
                                alignItems="center"
                                size="grow"
                              >
                                <Typography variant="h6" className="mb-2">
                                  {questionnaire.title}
                                </Typography>
                              </Grid2>
                              <Grid2
                                display="flex"
                                justifyContent="right"
                                alignItems="center"
                              >
                                <Chip
                                  size="small"
                                  icon={getStatusIcon(
                                    getQuestionnaireStatus(questionnaire.id)
                                  )}
                                  label={getQuestionnaireStatus(
                                    questionnaire.id
                                  ).replace('_', ' ')}
                                  color={getStatusColor(
                                    getQuestionnaireStatus(questionnaire.id)
                                  )}
                                />
                              </Grid2>
                            </Grid2>
                          </AccordionSummary>

                          <AccordionDetails>
                            <Typography variant="h6" className="mb-2">
                              Description
                            </Typography>

                            <Typography
                              variant="body1"
                              word-wrap="break-word"
                              className="mb-2"
                            >
                              {questionnaire.description}
                            </Typography>

                            <Grid2 container spacing={1}>
                              <Grid2
                                display="flex"
                                justifyContent="left"
                                alignItems="center"
                                size="grow"
                              >
                                <Typography variant="h6" className="mb-2">
                                  Submissions
                                </Typography>
                              </Grid2>
                              <Grid2
                                display="flex"
                                justifyContent="right"
                                alignItems="center"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    window.open(questionnaire.pdfURL)
                                  }
                                  title="Download Form"
                                >
                                  <Download size={18} />
                                </IconButton>
                              </Grid2>
                              {questionnaire.questions.length > 0 && (
                                <Grid2
                                  display="flex"
                                  justifyContent="right"
                                  alignItems="center"
                                >
                                  <Button
                                    variant="contained"
                                    onClick={() =>
                                      handleEditButton(questionnaire.id)
                                    }
                                  >
                                    Edit
                                  </Button>
                                </Grid2>
                              )}
                              <Grid2
                                display="flex"
                                justifyContent="right"
                                alignItems="center"
                              >
                                <Button
                                  variant="contained"
                                  startIcon={<Upload size={16} />}
                                  onClick={() => {
                                    setFormType(questionnaire.id);
                                    setUploadDialogOpen(true);
                                  }}
                                >
                                  Upload
                                </Button>
                              </Grid2>
                            </Grid2>

                            <List>
                              {getQuestionnaireSubmission(questionnaire.id) &&
                                getQuestionnaireSubmission(
                                  questionnaire.id
                                )?.versions.map((form) => {
                                  const student = projectMembers.find(
                                    (m) => m.userId === form.uploadedBy
                                  );
                                  return (
                                    <ListItem
                                      key={form.versionNumber}
                                      className="flex items-center p-4 border-b last:border-b-0"
                                    >
                                      <div className="flex-grow">
                                        <Typography
                                          variant="subtitle1"
                                          className="font-medium"
                                        >
                                          {`Submission ${form.versionNumber}`}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Responsible: {student?.firstName}{' '}
                                          {student?.lastName} • Last updated:{' '}
                                          {form.uploadedAt.toLocaleDateString()}
                                        </Typography>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Chip
                                          size="small"
                                          icon={getStatusIcon(form.status)}
                                          label={form.status.replace('_', ' ')}
                                          color={getStatusColor(form.status)}
                                        />
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            window.open(form.fileUrl)
                                          }
                                          title="View Submission"
                                        >
                                          <Eye size={16} />
                                        </IconButton>
                                      </div>
                                    </ListItem>
                                  );
                                })}
                              {!getQuestionnaireSubmission(
                                questionnaire.id
                              ) && <div>No Submissions</div>}
                            </List>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )
                )}
            </Box>
          ))}
        </Box>

        {/* Upload Dialog */}
        <Dialog
          open={uploadDialogOpen}
          onClose={() => {
            setUploadDialogOpen(false);
            resetUploadForm();
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Upload Project Form</DialogTitle>
          <DialogContent>
            <Box className="space-y-4 mt-4">
              <FormControl fullWidth>
                <InputLabel>Responsible Student</InputLabel>
                <Select
                  value={responsibleStudentId}
                  label="Responsible Student"
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    setResponsibleStudentId(selectedId);

                    // Find the selected student and set their name
                    const selectedStudent = projectMembers.find(
                      (member) => member.userId === selectedId
                    );
                    if (selectedStudent) {
                      setResponsibleStudentName(
                        `${selectedStudent.firstName} ${selectedStudent.lastName}`
                      );
                    }
                  }}
                >
                  {projectMembers.map((member) => (
                    <MenuItem key={member.userId} value={member.userId}>
                      {member.firstName} {member.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button variant="outlined" component="label" fullWidth>
                {selectedFile ? selectedFile.name : 'Select PDF File'}
                <input
                  type="file"
                  hidden
                  accept=".pdf"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedFile(e.target.files[0]);
                    }
                  }}
                />
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setUploadDialogOpen(false);
                resetUploadForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || !responsibleStudentId || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Form Filler Dialog */}
        <FormFiller
          open={formFillerOpen}
          onClose={() => setFormFillerOpen(false)}
          user={user}
          project={project}
          forms={forms}
          selectedQuestionnaire={
            questionnaires.filter(
              (obj) => obj.id === selectedQuestionnaireID
            )[0]
          }
        ></FormFiller>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectDocuments;
